import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  BillingRow,
  InvoiceRow,
  PaymentAccountDetails,
  PaymentReceivedRow,
} from "./paymentsTypes";
import { SEED_BILLING, SEED_INVOICES } from "./paymentsMockData";
import {
  getStoredPaymentAccount,
  PAYMENTS_BILLING_STORAGE_KEY,
  PAYMENTS_INVOICES_STORAGE_KEY,
  setStoredPaymentAccount,
} from "../lib/paymentsStorage";

function readJsonList<T>(key: string, fallback: T[]): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return fallback;
    return parsed as T[];
  } catch {
    return fallback;
  }
}

function writeJsonList<T>(key: string, rows: T[]): void {
  localStorage.setItem(key, JSON.stringify(rows));
}

interface PaymentsContextValue {
  account: PaymentAccountDetails | null;
  saveAccount: (details: PaymentAccountDetails) => void;
  /** Received payments (empty until connected to API). */
  payments: PaymentReceivedRow[];
  invoices: InvoiceRow[];
  setInvoices: React.Dispatch<React.SetStateAction<InvoiceRow[]>>;
  updateInvoice: (id: string, patch: Partial<InvoiceRow>) => void;
  deleteInvoice: (id: string) => void;
  billing: BillingRow[];
  setBilling: React.Dispatch<React.SetStateAction<BillingRow[]>>;
  updateBilling: (id: string, patch: Partial<BillingRow>) => void;
  deleteBilling: (id: string) => void;
}

const PaymentsContext = createContext<PaymentsContextValue | null>(null);

export function PaymentsProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<PaymentAccountDetails | null>(() =>
    getStoredPaymentAccount()
  );
  const [invoices, setInvoices] = useState<InvoiceRow[]>(() =>
    readJsonList(PAYMENTS_INVOICES_STORAGE_KEY, SEED_INVOICES)
  );
  const [billing, setBilling] = useState<BillingRow[]>(() =>
    readJsonList(PAYMENTS_BILLING_STORAGE_KEY, SEED_BILLING)
  );
  const [payments] = useState<PaymentReceivedRow[]>([]);

  useEffect(() => {
    writeJsonList(PAYMENTS_INVOICES_STORAGE_KEY, invoices);
  }, [invoices]);

  useEffect(() => {
    writeJsonList(PAYMENTS_BILLING_STORAGE_KEY, billing);
  }, [billing]);

  const saveAccount = useCallback((details: PaymentAccountDetails) => {
    setStoredPaymentAccount(details);
    setAccount(details);
  }, []);

  const updateInvoice = useCallback((id: string, patch: Partial<InvoiceRow>) => {
    setInvoices((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const deleteInvoice = useCallback((id: string) => {
    setInvoices((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const updateBilling = useCallback((id: string, patch: Partial<BillingRow>) => {
    setBilling((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const deleteBilling = useCallback((id: string) => {
    setBilling((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      account,
      saveAccount,
      payments,
      invoices,
      setInvoices,
      updateInvoice,
      deleteInvoice,
      billing,
      setBilling,
      updateBilling,
      deleteBilling,
    }),
    [
      account,
      saveAccount,
      payments,
      invoices,
      billing,
      updateInvoice,
      deleteInvoice,
      updateBilling,
      deleteBilling,
    ]
  );

  return <PaymentsContext.Provider value={value}>{children}</PaymentsContext.Provider>;
}

export function usePayments(): PaymentsContextValue {
  const ctx = useContext(PaymentsContext);
  if (!ctx) throw new Error("usePayments must be used within PaymentsProvider");
  return ctx;
}
